import { Page, View, Text, Link } from '@react-pdf/renderer';
import { styles } from './styles';
import {
  AgendaSpeaker,
  PublicEventDetailsResponse,
} from '@hubs101/booking-api-skd-client/lib/types';
import { globalStyles } from '../../styles';
import { useTranslation } from 'react-i18next';
import { getImageSrc } from '@utils/fn';
import { APP_HOST } from '@utils/apiHooks';
import { SafeImage } from '@composites/PDF/AgendaPdfContent/components/SafeImage';

export interface AllSpeakerPageProps {
  event: PublicEventDetailsResponse;
  footer: JSX.Element;
  agendaSpeakers: AgendaSpeaker[];
}

const FIRST_PAGE_LIMIT = 28;
const OTHER_PAGE_LIMIT = 29;

const AllSpeakerPage = ({
  event,
  footer,
  agendaSpeakers,
}: AllSpeakerPageProps) => {
  const { t } = useTranslation();

  const filteredSpeakers =
    agendaSpeakers?.filter(
      speaker => speaker?.company?.logo && speaker?.images?.picture
    ) || [];

  // Build flat render list with dynamic chunk sizes
  const renderItems: Array<
    { type: 'speaker'; data: AgendaSpeaker } | { type: 'button'; key: string }
  > = [];

  let index = 0;
  let chunkSize = FIRST_PAGE_LIMIT;
  let chunkCounter = 0;

  while (index < filteredSpeakers.length) {
    const chunk = filteredSpeakers.slice(index, index + chunkSize);

    chunk.forEach(speaker => {
      renderItems.push({ type: 'speaker', data: speaker });
    });

    // Always add button after each chunk
    renderItems.push({
      type: 'button',
      key: `button-${chunkCounter}`,
    });

    index += chunkSize;
    chunkSize = OTHER_PAGE_LIMIT; // after first iteration switch to 29
    chunkCounter++;
  }

  return (
    <Page size={{ width: 1440, height: 800 }} style={styles.page}>
      <View style={styles.fullWidthContainer}>
        <View style={styles.galleryContainer}>
          <View style={{ width: 220, height: 140 }}>
            <Text style={styles.pageTitle}>{t('speaker')}</Text>
            <View
              style={{
                ...globalStyles.lineBar,
                alignSelf: 'flex-start',
                borderColor: event?.design?.themeColor ?? 'black',
                marginTop: 20,
                marginLeft: 0,
                marginBottom: 5,
                width: 140,
              }}
            />
          </View>

          {/* Render speakers + buttons */}
          {renderItems.map((item, idx) => {
            if (item.type === 'button') {
              return (
                <View
                  key={item.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 220,
                    height: 110,
                  }}
                  wrap={false}
                >
                  <Link
                    src={`${APP_HOST}/events/${event?.slug}${t(
                      'speakers-url'
                    )}`}
                    style={{
                      ...styles.button,
                      backgroundColor: event?.design?.themeColor ?? '#000',
                      color: 'white',
                      width: 220,
                    }}
                  >
                    {t('show-all-speakers')}!
                  </Link>
                </View>
              );
            }

            const speaker = item.data;
            const companyLogoSrc = getImageSrc(speaker?.company?.logo);
            const speakerImageSrc = getImageSrc(speaker?.images?.picture);

            if (!companyLogoSrc || !speakerImageSrc) return null;

            return (
              <View
                key={`speaker-${speaker.profile ?? idx}`}
                style={styles.speakerItemWrapper}
                wrap={false}
              >
                <View style={styles.leftColumn}>
                  <SafeImage
                    style={styles.speakerImage}
                    src={speakerImageSrc}
                  />
                  <SafeImage
                    style={styles.companyLogoImage}
                    src={companyLogoSrc}
                  />
                </View>

                <View style={styles.rightColumn}>
                  <Text style={styles.speakerNameText}>
                    {speaker?.first_name} {speaker?.last_name}
                  </Text>
                  <Text style={styles.speakerCompanyText}>
                    {speaker?.company?.name}
                  </Text>
                  <Text style={styles.speakerJobTitleText}>
                    {speaker?.job?.title}
                  </Text>
                </View>
              </View>
            );
          })}
        </View>
      </View>

      {footer}
    </Page>
  );
};

export default AllSpeakerPage;
